import { supabase } from '@/integrations/supabase/client';

export interface ReAnalysisSession {
  id: string;
  bot_id: string;
  trading_file_id: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  started_at: string;
  completed_at?: string;
  csv_trades_count: number;
  realtime_trades_count: number;
  total_trades_analyzed: number;
  patterns_found: number;
  patterns_updated: number;
  patterns_created: number;
  processing_time_ms?: number;
  analysis_time_ms?: number;
  analysis_config: any;
  error_message?: string;
  success_message?: string;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export interface SessionFilters {
  status?: string;
  botId?: string;
  tradingFileId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const ReAnalysisHistoryService = {
  /**
   * Get re-analysis sessions with optional filters
   */
  async getSessions(filters?: SessionFilters): Promise<{ success: boolean; data?: ReAnalysisSession[]; error?: string }> {
    try {
      let query = supabase
        .from('re_analysis_sessions')
        .select(`
          *,
          bots!inner(name, type),
          trading_files!inner(filename)
        `)
        .order('started_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.botId) {
        query = query.eq('bot_id', filters.botId);
      }

      if (filters?.tradingFileId) {
        query = query.eq('trading_file_id', filters.tradingFileId);
      }

      if (filters?.dateFrom) {
        query = query.gte('started_at', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('started_at', filters.dateTo);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching re-analysis sessions:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: (data || []) as ReAnalysisSession[] };
    } catch (error) {
      console.error('Error in getSessions:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Get a specific session by ID
   */
  async getSessionById(sessionId: string): Promise<{ success: boolean; data?: ReAnalysisSession; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('re_analysis_sessions')
        .select(`
          *,
          bots!inner(name, type, signal_token),
          trading_files!inner(filename, file_size, uploaded_at)
        `)
        .eq('id', sessionId)
        .single();

      if (error) {
        console.error('Error fetching session:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as ReAnalysisSession };
    } catch (error) {
      console.error('Error in getSessionById:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Get sessions for a specific bot
   */
  async getSessionsForBot(botId: string): Promise<{ success: boolean; data?: ReAnalysisSession[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('re_analysis_sessions')
        .select('*')
        .eq('bot_id', botId)
        .order('started_at', { ascending: false });

      if (error) {
        console.error('Error fetching bot sessions:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: (data || []) as ReAnalysisSession[] };
    } catch (error) {
      console.error('Error in getSessionsForBot:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Get sessions for a specific trading file
   */
  async getSessionsForFile(fileId: string): Promise<{ success: boolean; data?: ReAnalysisSession[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('re_analysis_sessions')
        .select('*')
        .eq('trading_file_id', fileId)
        .order('started_at', { ascending: false });

      if (error) {
        console.error('Error fetching file sessions:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: (data || []) as ReAnalysisSession[] };
    } catch (error) {
      console.error('Error in getSessionsForFile:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Delete a re-analysis session
   */
  async deleteSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('re_analysis_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) {
        console.error('Error deleting session:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteSession:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Delete multiple re-analysis sessions
   */
  async deleteBulkSessions(sessionIds: string[]): Promise<{ 
    success: boolean; 
    deletedCount?: number; 
    failedCount?: number; 
    errors?: string[]; 
    error?: string 
  }> {
    try {
      if (sessionIds.length === 0) {
        return { success: true, deletedCount: 0, failedCount: 0 };
      }

      const { error } = await supabase
        .from('re_analysis_sessions')
        .delete()
        .in('id', sessionIds);

      if (error) {
        console.error('Error deleting sessions:', error);
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        deletedCount: sessionIds.length, 
        failedCount: 0 
      };
    } catch (error) {
      console.error('Error in deleteBulkSessions:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Get statistics for all sessions
   */
  async getStatistics(): Promise<{ 
    success: boolean; 
    data?: {
      totalSessions: number;
      completedSessions: number;
      failedSessions: number;
      runningSessions: number;
      averageProcessingTime: number;
      totalPatternsProcessed: number;
    }; 
    error?: string 
  }> {
    try {
      const { data, error } = await supabase
        .from('re_analysis_sessions')
        .select('status, processing_time_ms, patterns_found');

      if (error) {
        console.error('Error fetching statistics:', error);
        return { success: false, error: error.message };
      }

      const sessions = data || [];
      const completedSessions = sessions.filter(s => s.status === 'completed');
      
      const stats = {
        totalSessions: sessions.length,
        completedSessions: completedSessions.length,
        failedSessions: sessions.filter(s => s.status === 'failed').length,
        runningSessions: sessions.filter(s => s.status === 'running').length,
        averageProcessingTime: completedSessions.length > 0 
          ? Math.round(completedSessions.reduce((sum, s) => sum + (s.processing_time_ms || 0), 0) / completedSessions.length)
          : 0,
        totalPatternsProcessed: sessions.reduce((sum, s) => sum + (s.patterns_found || 0), 0)
      };

      return { success: true, data: stats };
    } catch (error) {
      console.error('Error in getStatistics:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
};